'use client';

import * as React from 'react';
import { RootState } from '@/store';
import { refreshActiveTable } from '@/store/reducers/table-slice';
import { hideModal } from '@/store/reducers/ui-slice';
import { AdminRoles, AuthMethod, CreateUserDto, PlayerSubInfo } from '@alien-platform/bo-sdk-v2';
import { MethodResponse } from '@alien-platform/cms-bo';
import { IUserPermissions } from '@ap-db-worker/common';
import { Button } from '@mui/material';
import Stack from '@mui/material/Stack';
import { useDispatch, useSelector } from 'react-redux';
import { z } from 'zod';

import { CustomConfigInputProps, InputType } from '@/types/general';
import { buildZodSchemaFromFields } from '@/lib/helpers';
import { CustomConfigInputV2 } from '@/components/core/custom-config-input-v2';
import { toast } from '@/components/core/toaster';

export function CreateStaffModalContent(): React.JSX.Element | null {
  const { user } = useSelector((state: RootState) => state.user);

  const isSuperAdmin = user?.role === AdminRoles.SUPER_ADMIN;

  const operatorsArray = [{ label: 'Demo Operator', value: 'B2C1002' }];

  const rolesArray = isSuperAdmin
    ? Object.values(AdminRoles).map((role) => ({
        label: role.replace(/_/g, ' '),
        value: role,
      }))
    : Object.values(AdminRoles)
        .filter((item) => item !== AdminRoles.ADMIN && item !== AdminRoles.SUPER_ADMIN && item !== AdminRoles.DEV_OPS)
        .map((role) => ({
          label: role.replace(/_/g, ' '),
          value: role,
        }));

  const [dto, setDto] = React.useState<CreateUserDto>({
    email: '',
    password: '',
    role: AdminRoles.OPERATOR_ADMIN,
    userCode: '',
    opId: '',
    authMethod: AuthMethod.PASSWORD,
    nickname: '',
    timezone: '',
    playerSubInfoType: PlayerSubInfo.USERNAME,
  });

  const generalInputs: CustomConfigInputProps[] = [
    {
      name: 'email',
      label: 'Email',
      required: false,
      value: dto.email,
      type: InputType.INPUT,
      size: { xs: 12, lg: 6 },
      zod: z.string().email(),
    },
    {
      name: 'authMethod',
      label: 'Authentication Method',
      required: false,
      value: dto.authMethod,
      type: InputType.SELECT,
      menuItems: Object.values(AuthMethod).map((item) => ({ label: item.replace(/_/g, ' '), value: item })),
      size: { xs: 12, lg: 6 },
      zod: z.string(),
      hidden: !isSuperAdmin,
    },

    {
      name: 'password',
      label: 'Password',
      required: false,
      value: dto.password,
      type: InputType.PASSWORD,
      size: { xs: 12, lg: 6 },
      zod: z.string().min(6, 'Password must be at least 6 characters').optional().or(z.literal('')),
      hidden: dto.authMethod !== AuthMethod.PASSWORD,
    },

    {
      name: 'nickname',
      label: 'Nickname',
      required: false,
      value: dto.nickname,
      type: InputType.INPUT,
      size: { xs: 12, lg: 6 },
      zod: z.string().min(4, 'Nickame must be at least 4 characters'),
    },
    {
      name: 'userCode',
      label: 'User Code',
      required: false,
      value: dto.userCode,
      type: InputType.INPUT,
      size: { xs: 12, lg: 6 },
      zod: z.string().min(3, 'User code must be at least 3 characters'),
    },

    {
      name: 'role',
      label: 'User Role',
      required: false,
      value: dto.role,
      type: InputType.SELECT,
      menuItems: rolesArray,
      size: { xs: 12, lg: 6 },
      zod: z.string(),
    },
    {
      name: 'opId',
      label: 'Operator',
      required: false,
      value: dto.opId,
      type: InputType.SELECT,
      menuItems: operatorsArray,
      size: { xs: 12, lg: 6 },
    },
  ];

  const newStaffSchema = buildZodSchemaFromFields(generalInputs);
  // const newStaffSchema = buildZodSchemaFromFields(generalInputs).refine(
  //   (data) => data.password === data.confirmPassword,
  //   {
  //     message: 'Passwords do not match',
  //     path: ['confirmPassword'], // attach error to confirmPassword field
  //   }
  // );
  type NewStaff = z.infer<typeof newStaffSchema>;
  const [formErrors, setFormErrors] = React.useState<Partial<Record<keyof NewStaff, string>>>({});

  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = React.useState(false);
  const handleClose = (): void => {
    dispatch(hideModal());
    setFormErrors({});
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setFormErrors({}); //to clear previous errors if any

    const parseResult = newStaffSchema.safeParse(dto);

    if (!parseResult.success) {
      const errors: Partial<Record<keyof NewStaff, string>> = {};

      const fieldErrors = parseResult.error.flatten().fieldErrors;
      for (const key in fieldErrors) {
        if (fieldErrors[key] && fieldErrors[key].length > 0) {
          errors[key] = fieldErrors[key][0];
        }
      }
      setFormErrors(errors);
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`/api/users`, {
        method: 'POST',
        body: JSON.stringify({ ...dto }),
      });
      const result: MethodResponse<IUserPermissions> = await response.json();
      if (response.ok && result.success) {
        toast.success('User created successfully!');
        dispatch(hideModal());
        dispatch(refreshActiveTable());
      } else {
        toast.error(result.messages?.[0] || 'Error: 500 | Unable to create user');
      }
    } catch {
      toast.error('Error: 500 | Unable to crerate user');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={3}>
        {generalInputs.map((input, index) => (
          <CustomConfigInputV2
            key={index}
            defaultData={dto}
            setDefaultData={setDto}
            error={formErrors[input.name]}
            onChange={(__) => {
              setFormErrors((prev) => {
                const { [input.name]: _removed, ...rest } = prev;
                return rest;
              });
            }}
            {...input}
          />
        ))}
      </Stack>
      <Stack direction="row" spacing={3} sx={{ mt: 3, justifyContent: 'initial' }}>
        <Button type="button" color="secondary" onClick={handleClose} sx={{ flex: '1 1 auto' }}>
          Close
        </Button>

        <Button loading={isLoading} onClick={handleSubmit} sx={{ flex: '1 1 auto' }} variant="contained">
          Save Changes
        </Button>
      </Stack>
    </form>
  );
}
